import { motion } from "framer-motion";
import { useEffect, useState, useRef } from "react";
import { Cpu, Shield, GitBranch, Lock, Zap, Layers } from "lucide-react";

const layers = [
  {
    id: "identity",
    icon: Lock,
    label: "Identity Binding",
    sub: "Layer 1",
    desc: "Piggyback off Okta/Entra. Agent inherits employee ACLs wholesale.",
    color: "#94A3B8",
  },
  {
    id: "mcp",
    icon: Layers,
    label: "MCP Sidecar",
    sub: "Layer 2",
    desc: "Decouple reasoning from execution. No direct API credentials in the agent.",
    color: "#008080",
  },
  {
    id: "taint",
    icon: GitBranch,
    label: "Taint Tracking",
    sub: "Layer 3",
    desc: "Dynamic information-flow control. Confidentiality labels propagate through action chains.",
    color: "#00B3B3",
  },
  {
    id: "classifier",
    icon: Cpu,
    label: "ML Classifier",
    sub: "Layer 4",
    desc: "Gradient Boosted Trees + Isolation Forests. Sub-millisecond, deterministic verdicts.",
    color: "#00E5FF",
  },
  {
    id: "gate",
    icon: Shield,
    label: "Execution Gate",
    sub: "Layer 5",
    desc: "Allow, block, or escalate to human-in-the-loop. Full audit trail.",
    color: "#10B981",
  },
];

function AnimatedPacket({ active }) {
  return (
    <motion.div
      className="absolute left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#00E5FF]"
      style={{ boxShadow: "0 0 12px rgba(0, 229, 255, 0.8)" }}
      animate={
        active
          ? { top: ["0%", "100%"], opacity: [1, 1, 0] }
          : { opacity: 0 }
      }
      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
    />
  );
}

export default function Solution() {
  const [activeLayer, setActiveLayer] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setActiveLayer((prev) => (prev + 1) % layers.length);
    }, 3000);
    return () => clearInterval(intervalRef.current);
  }, []);

  return (
    <section
      id="solution"
      data-testid="solution-section"
      className="relative py-24 lg:py-32 bg-[#0D0F14] border-y border-[#1A2332]"
    >
      <div className="absolute inset-0 grid-bg opacity-20" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-xs font-mono text-[#00E5FF] uppercase tracking-[0.2em] flex items-center gap-2 mb-4">
            <Zap size={14} />
            The LeapForce Architecture
          </span>
          <h2
            data-testid="solution-headline"
            className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white mb-4"
          >
            Security at the{" "}
            <span className="text-[#00E5FF]">Action Chain</span> Level
          </h2>
          <p className="text-base text-[#94A3B8] max-w-2xl leading-relaxed">
            Five deterministic layers between your AI agents and your enterprise tools. 
            No LLMs in the security loop. Sub-millisecond verdicts.
          </p>
        </motion.div>

        {/* Architecture Flow */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: Layer selector */}
          <div className="lg:col-span-5 space-y-0">
            {layers.map((layer, i) => (
              <motion.button
                key={layer.id}
                data-testid={`solution-layer-${layer.id}`}
                onClick={() => {
                  setActiveLayer(i);
                  clearInterval(intervalRef.current);
                }}
                className={`w-full text-left p-5 border border-[#1A2332] transition-all flex items-start gap-4 ${
                  activeLayer === i
                    ? "bg-[#1A1D24] border-l-2"
                    : "bg-[#0D0F14] hover:bg-[#0D0F14]/80"
                }`}
                style={{
                  borderLeftColor: activeLayer === i ? layer.color : undefined,
                }}
                whileHover={{ x: 4 }}
              >
                <div
                  className="w-9 h-9 flex items-center justify-center shrink-0 border"
                  style={{
                    borderColor: activeLayer === i ? layer.color : "#1A2332",
                    color: activeLayer === i ? layer.color : "#475569",
                  }}
                >
                  <layer.icon size={16} />
                </div>
                <div>
                  <span
                    className="text-[10px] font-mono uppercase tracking-[0.2em] block mb-1"
                    style={{
                      color: activeLayer === i ? layer.color : "#475569",
                    }}
                  >
                    {layer.sub}
                  </span>
                  <span
                    className={`text-sm font-semibold block ${
                      activeLayer === i ? "text-white" : "text-[#94A3B8]"
                    }`}
                  >
                    {layer.label}
                  </span>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Right: Animated architecture visual */}
          <div className="lg:col-span-7 relative">
            <div className="bg-[#050508] border border-[#1A2332] p-8 h-full min-h-[400px] flex flex-col justify-center">
              {/* Animated vertical pipeline */}
              <div className="relative flex flex-col items-center gap-0">
                {layers.map((layer, i) => (
                  <div key={layer.id} className="flex flex-col items-center">
                    {/* Node */}
                    <motion.div
                      className={`relative z-10 flex items-center gap-3 px-5 py-3 border transition-all ${
                        i === activeLayer
                          ? "border-opacity-100 bg-[#1A1D24]"
                          : "border-[#1A2332] bg-[#0D0F14]"
                      }`}
                      style={{
                        borderColor:
                          i === activeLayer ? layer.color : undefined,
                      }}
                      animate={
                        i === activeLayer
                          ? { scale: 1.05, boxShadow: `0 0 20px ${layer.color}33` }
                          : { scale: 1, boxShadow: "none" }
                      }
                      transition={{ duration: 0.3 }}
                    >
                      <layer.icon
                        size={16}
                        style={{
                          color:
                            i === activeLayer ? layer.color : "#475569",
                        }}
                      />
                      <span
                        className="font-mono text-xs"
                        style={{
                          color: i === activeLayer ? "#fff" : "#94A3B8",
                        }}
                      >
                        {layer.label}
                      </span>
                      {i === activeLayer && (
                        <motion.div
                          className="w-2 h-2 rounded-full ml-2"
                          style={{ backgroundColor: layer.color }}
                          animate={{ opacity: [1, 0.3, 1] }}
                          transition={{ repeat: Infinity, duration: 1.5 }}
                        />
                      )}
                    </motion.div>

                    {/* Connector line */}
                    {i < layers.length - 1 && (
                      <div className="relative w-px h-8 bg-[#1A2332]">
                        <AnimatedPacket active={i === activeLayer} />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Description */}
              <motion.div
                key={activeLayer}
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                className="mt-8 border-t border-[#1A2332] pt-6"
              >
                <p className="text-sm text-[#94A3B8] leading-relaxed">
                  <span className="text-white font-semibold">
                    {layers[activeLayer].label}:
                  </span>{" "}
                  {layers[activeLayer].desc}
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
